import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useAuthStore } from '../useAuthStore'
import { apiClient } from '../../services/api'
import type { LoginRequest, LoginResponse, User } from '../../types/api'

/**
 * Authentication hook for login/logout operations
 */
export const useAuth = () => {
  const queryClient = useQueryClient()
  const { login: loginStore, logout: logoutStore, user, token } = useAuthStore()

  // Login mutation
  const loginMutation = useMutation({
    mutationFn: async (credentials: LoginRequest) => {
      const response = await apiClient.post<LoginResponse>('/auth/admin/login', credentials)
      return response.data
    },
    onSuccess: (data) => {
      // Store auth data in Zustand
      // Handle both user and admin response structures
      const user = data.user || data.admin
      loginStore(user, data.token)
      
      // Invalidate all queries to force refresh with new auth
      queryClient.invalidateQueries()
    },
    onError: (error: any) => {
      console.error('Login failed:', error)
      // Error will be handled by the component
    }
  })

  // Logout mutation
  const logoutMutation = useMutation({
    mutationFn: async () => {
      // Optional: Call logout endpoint if it exists
      try {
        await apiClient.post('/auth/logout')
      } catch (error) {
        // Logout locally even if server call fails
        console.warn('Server logout failed, proceeding with local logout')
      }
    },
    onSuccess: () => {
      // Clear auth state
      logoutStore()
      
      // Clear all cached queries
      queryClient.clear()
    }
  })

  // Get current user info
  const userQuery = useQuery({
    queryKey: ['user', 'current'],
    queryFn: async () => {
      const response = await apiClient.get<{ user: User }>('/auth/me')
      return response.data.user
    },
    enabled: !!token, // Only run if we have a token
    staleTime: 5 * 60 * 1000, // 5 minutes
    retry: false // Don't retry if unauthorized
  })

  return {
    // State
    user,
    token,
    isAuthenticated: !!token,
    
    // Mutations
    login: loginMutation.mutate,
    logout: logoutMutation.mutate,
    
    // Loading states
    isLoggingIn: loginMutation.isPending,
    isLoggingOut: logoutMutation.isPending,
    
    // Errors
    loginError: loginMutation.error,
    logoutError: logoutMutation.error,
    
    // User query
    currentUser: userQuery.data,
    isLoadingUser: userQuery.isLoading,
    userError: userQuery.error
  }
}

/**
 * Hook for LINE OAuth authentication (future implementation)
 */
export const useLineAuth = () => {
  const queryClient = useQueryClient()
  const { login: loginStore } = useAuthStore()

  const lineLoginMutation = useMutation({
    mutationFn: async (code: string) => {
      const response = await apiClient.post<LoginResponse>('/auth/line/callback', { code })
      return response.data
    },
    onSuccess: (data) => {
      loginStore(data.user, data.token)
      queryClient.invalidateQueries()
    }
  })

  const getLineAuthUrl = () => {
    // This would generate the LINE OAuth URL
    // For now, return a placeholder
    return '#'
  }

  return {
    loginWithLine: lineLoginMutation.mutate,
    isLoggingIn: lineLoginMutation.isPending,
    error: lineLoginMutation.error,
    getLineAuthUrl
  }
}